import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Link } from "react-router-dom";
import { 
  LayoutDashboard, Crown, PiggyBank, ClipboardList, Shield,
  Calendar, Ticket, Users, FileText, MessageSquare,
  Heart, Handshake, Mail, Video, HardDrive, CalendarSync,
  ShoppingBag, Lightbulb, Gamepad2, Gift, Target, DollarSign,
  Package, UtensilsCrossed, ListTodo, Check, X, Sparkles,
  ChevronRight, Zap, Building2, QrCode, ClipboardCheck
} from "lucide-react";

type PlanFilter = 'all' | 'basic' | 'standard';

interface Feature {
  id: string;
  name: string;
  description: string;
  detailedDescription: string[];
  icon: React.ComponentType<{ className?: string }>;
  plan: 'basic' | 'standard';
}

interface FeatureCategory {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  features: Feature[];
}

// Categories that should show BOTH plan badges when "All Features" filter is selected
const showBothBadgesCategories = ['dashboards', 'administration', 'financial'];
// Individual features that should show both badges
const showBothBadgesFeatures = ['meeting-management', 'affiliate-shop'];

const featureCategories: FeatureCategory[] = [
  {
    id: 'dashboards',
    name: 'Core Features',
    description: 'Essential dashboards for every committee member',
    icon: LayoutDashboard,
    features: [
      {
        id: 'main-dashboard',
        name: 'Main Dashboard',
        description: 'Central hub with stats, upcoming events, and member directory.',
        detailedDescription: [
          'Overview of key PTA statistics and metrics',
          'Quick access to upcoming events calendar',
          'Member directory with contact information',
          'Goal tracking with visual progress bars',
          'Calendar view with month/week/day options'
        ],
        icon: LayoutDashboard,
        plan: 'basic'
      },
      {
        id: 'chair-dashboard',
        name: 'Chair Dashboard',
        description: 'Strategic oversight with goal management and task assignment.',
        detailedDescription: [
          'Goal management with status tracking',
          'Meeting scheduling and overview',
          'Task assignment to committee members',
          'AI-powered event planning assistance',
          'Committee performance insights'
        ],
        icon: Crown,
        plan: 'basic'
      },
      {
        id: 'treasurer-dashboard',
        name: 'Treasurer Dashboard',
        description: 'Complete financial management with reporting and tracking.',
        detailedDescription: [
          'Transaction tracking with categories',
          'Expense claims workflow',
          'Cash float management with denomination counting',
          'Bank access log tracking',
          'Fund allocations and ring-fencing',
          'PDF financial reports generation'
        ],
        icon: PiggyBank,
        plan: 'basic'
      },
      {
        id: 'secretary-dashboard',
        name: 'Secretary Dashboard',
        description: 'Meeting management, agendas, and member communications.',
        detailedDescription: [
          'Meeting scheduling and management',
          'Agenda creation and distribution',
          'Minutes recording and storage',
          'Email communications to members',
          'Document organization'
        ],
        icon: ClipboardList,
        plan: 'basic'
      },
      {
        id: 'trustee-dashboard',
        name: 'Trustee Dashboard',
        description: 'Governance policies, audits, and approval thresholds.',
        detailedDescription: [
          'Governance policy management',
          'Audit requirement tracking',
          'Approval threshold configuration',
          'Compliance monitoring',
          'Risk assessment tools'
        ],
        icon: Shield,
        plan: 'basic'
      }
    ]
  },
  {
    id: 'administration',
    name: 'Administration',
    description: 'Manage your PTA members, roles, and admin settings',
    icon: Users,
    features: [
      {
        id: 'member-management',
        name: 'Member Management',
        description: 'Manage members, roles, and permissions.',
        detailedDescription: [
          'Member directory with profiles',
          'Role assignment (Chair, Treasurer, etc.)',
          'Deputy role support',
          'Member invitation system',
          'Contact information management'
        ],
        icon: Users,
        plan: 'basic'
      },
      {
        id: 'pta-admin-dashboard',
        name: 'PTA Admin Dashboard',
        description: 'Full administrative control over your PTA settings.',
        detailedDescription: [
          'PTA settings and configuration',
          'Member role management',
          'Access control and permissions',
          'PTA branding and customization',
          'Subscription and billing management'
        ],
        icon: Shield,
        plan: 'standard'
      }
    ]
  },
  {
    id: 'events',
    name: 'Event Management',
    description: 'Plan, manage, and execute successful PTA events',
    icon: Calendar,
    features: [
      {
        id: 'basic-events',
        name: 'Basic Events',
        description: 'Create and manage events with volunteer tracking.',
        detailedDescription: [
          'Create and schedule events',
          'Basic event details and descriptions',
          'Location and time management',
          'Simple volunteer tracking',
          'Event status updates'
        ],
        icon: Calendar,
        plan: 'basic'
      },
      {
        id: 'advanced-events',
        name: 'Advanced Events',
        description: 'Full ticketing, AI planner, stalls, and check-in system.',
        detailedDescription: [
          'Complete ticketing system with pricing',
          'AI-powered event planning assistance',
          'Stall management with applications',
          'Volunteer signup with role assignments',
          'QR code event check-in',
          'Event templates for recurring events',
          'Catering assignments per event'
        ],
        icon: Ticket,
        plan: 'standard'
      }
    ]
  },
  {
    id: 'financial',
    name: 'Financial Tools',
    description: 'Comprehensive financial management and reporting',
    icon: PiggyBank,
    features: [
      {
        id: 'transactions',
        name: 'Transaction Tracking',
        description: 'Record income and expenses with categories.',
        detailedDescription: [
          'Income and expense recording',
          'Category and subcategory assignment',
          'Event linking for transactions',
          'Receipt attachment support',
          'Transaction search and filtering'
        ],
        icon: DollarSign,
        plan: 'basic'
      },
      {
        id: 'expense-claims',
        name: 'Expense Claims',
        description: 'Submit, approve, and track expense claims.',
        detailedDescription: [
          'Easy claim submission form',
          'Receipt upload and storage',
          'Approval workflow',
          'Payment status tracking',
          'Claim history and archiving'
        ],
        icon: ClipboardCheck,
        plan: 'basic'
      },
      {
        id: 'cash-floats',
        name: 'Cash Float Management',
        description: 'Track physical cash with denomination counting.',
        detailedDescription: [
          'Multiple float tracking',
          'Denomination counting interface',
          'Float assignment to events',
          'Count history logging',
          'Discrepancy alerts'
        ],
        icon: Package,
        plan: 'basic'
      },
      {
        id: 'fund-allocations',
        name: 'Fund Allocations',
        description: 'Allocate and ring-fence funds for specific goals.',
        detailedDescription: [
          'Allocate funds to goals',
          'Ring-fencing for protected funds',
          'Estimated spend date tracking',
          'Allocation status management',
          'Linked to goal progress'
        ],
        icon: Target,
        plan: 'basic'
      },
      {
        id: 'financial-reports',
        name: 'Financial Reports',
        description: 'Generate PDF reports for any date range.',
        detailedDescription: [
          'Customizable date ranges',
          'Professional PDF export',
          'Income vs expense summaries',
          'Category breakdowns',
          'Event-based reporting'
        ],
        icon: FileText,
        plan: 'basic'
      }
    ]
  },
  {
    id: 'communication',
    name: 'Communication & Community',
    description: 'Connect with members and build your community',
    icon: MessageSquare,
    features: [
      {
        id: 'meeting-management',
        name: 'Meeting Management',
        description: 'Schedule meetings, track attendance, record minutes.',
        detailedDescription: [
          'Meeting scheduling with invites',
          'Attendance tracking',
          'Agenda templates',
          'Minutes recording',
          'Action item tracking'
        ],
        icon: ClipboardList,
        plan: 'basic'
      },
      {
        id: 'communications-dashboard',
        name: 'Communications Dashboard',
        description: 'Facebook integration for direct posting.',
        detailedDescription: [
          'Facebook page connection',
          'Post creation and scheduling',
          'Post analytics and engagement',
          'Draft saving',
          'Link attachments'
        ],
        icon: MessageSquare,
        plan: 'standard'
      },
      {
        id: 'community-dashboard',
        name: 'Community Dashboard',
        description: 'Letter templates, sponsorship, and donor management.',
        detailedDescription: [
          'Letter templates with AI generation',
          'Sponsorship tracking and management',
          'Donor database with contact history',
          'Stall application form builder',
          'Thank you letter automation'
        ],
        icon: Heart,
        plan: 'standard'
      }
    ]
  },
  {
    id: 'integrations',
    name: 'Integrations',
    description: 'Connect with Google Workspace for seamless collaboration',
    icon: Zap,
    features: [
      {
        id: 'google-drive',
        name: 'Google Drive',
        description: 'Store and share PTA documents in the cloud.',
        detailedDescription: [
          'Connect your Google Drive',
          'Access PTA documents anywhere',
          'Automatic file organization',
          'Share documents with members',
          'Version history'
        ],
        icon: HardDrive,
        plan: 'standard'
      },
      {
        id: 'google-calendar',
        name: 'Google Calendar Sync',
        description: 'Sync all PTA events automatically.',
        detailedDescription: [
          'Two-way calendar sync',
          'Automatic event updates',
          'Meeting reminders',
          'Calendar sharing with members',
          'Multiple calendar support'
        ],
        icon: CalendarSync,
        plan: 'standard'
      },
      {
        id: 'google-meet',
        name: 'Google Meet',
        description: 'Create video meetings from meeting scheduler.',
        detailedDescription: [
          'One-click meeting creation',
          'Automatic meeting links',
          'Calendar integration',
          'Remote attendance support',
          'Recording capabilities'
        ],
        icon: Video,
        plan: 'standard'
      }
    ]
  },
  {
    id: 'engagement',
    name: 'Engagement Tools',
    description: 'Engage your community with fundraising and games',
    icon: Sparkles,
    features: [
      {
        id: 'affiliate-shop',
        name: 'Shop',
        description: 'Browse PTA products with commission earnings.',
        detailedDescription: [
          'Curated product recommendations',
          'Commission on purchases',
          'PTA-relevant product categories',
          'Easy sharing with parents',
          'Earnings tracking'
        ],
        icon: ShoppingBag,
        plan: 'basic'
      },
      {
        id: 'fundraising-library',
        name: 'Fundraising Library',
        description: '100+ fundraising ideas with filters and voting.',
        detailedDescription: [
          'Over 100 curated ideas',
          'Filter by season and location',
          'Community voting and ratings',
          'Detailed planning guides',
          'Save favorites for later'
        ],
        icon: Lightbulb,
        plan: 'basic'
      },
      {
        id: 'digital-raffle',
        name: 'Digital Raffle',
        description: 'Run raffles with automated draws and animations.',
        detailedDescription: [
          'Create unlimited raffles',
          'Animated prize draws',
          'Winner notifications',
          'Ticket sales tracking',
          'Prize management'
        ],
        icon: Gift,
        plan: 'standard'
      },
      {
        id: 'guessing-game',
        name: 'Guessing Game',
        description: 'Weight/name/quantity games with prizes.',
        detailedDescription: [
          'Multiple game types',
          'Entry management',
          'Winner determination',
          'Prize tracking',
          'Results display'
        ],
        icon: Gamepad2,
        plan: 'standard'
      },
      {
        id: 'bingo-caller',
        name: 'Bingo Caller',
        description: 'Interactive bingo with audio and QR codes.',
        detailedDescription: [
          'Animated number calling',
          'Audio announcements',
          'QR code player cards',
          'Winner verification',
          'Multiple game modes'
        ],
        icon: QrCode,
        plan: 'standard'
      },
      {
        id: 'fundraising-page',
        name: 'Public Fundraising Page',
        description: 'Public page with events, goals, and donations.',
        detailedDescription: [
          'Customizable public page',
          'Event showcase',
          'Goal progress display',
          'Online donations',
          'Community marketplace'
        ],
        icon: Target,
        plan: 'standard'
      }
    ]
  },
  {
    id: 'advanced',
    name: 'Advanced Management',
    description: 'Powerful tools for larger PTAs',
    icon: Building2,
    features: [
      {
        id: 'assets-dashboard',
        name: 'Assets Dashboard',
        description: 'Track assets, loans, and inter-PTA sharing.',
        detailedDescription: [
          'Complete asset inventory',
          'Loan management system',
          'Inter-PTA asset sharing',
          'Payment processing for loans',
          'Asset condition tracking',
          'Location management'
        ],
        icon: Package,
        plan: 'standard'
      },
      {
        id: 'catering-dashboard',
        name: 'Catering Dashboard',
        description: 'Stock, menus, allergens, and event assignments.',
        detailedDescription: [
          'Stock management with batches',
          'Menu item creation',
          'Allergen tracking and display',
          'Event catering assignments',
          'Supplier management',
          'Expiry date monitoring'
        ],
        icon: UtensilsCrossed,
        plan: 'standard'
      },
      {
        id: 'task-management',
        name: 'Task Management',
        description: 'Assign tasks with notifications and tracking.',
        detailedDescription: [
          'Create and assign tasks',
          'Due date tracking',
          'Priority levels',
          'Email notifications',
          'Progress monitoring',
          'Task completion tracking'
        ],
        icon: ListTodo,
        plan: 'standard'
      }
    ]
  }
];

// Features for comparison table
const comparisonFeatures = [
  // Basic features (included in both plans)
  { name: 'Main Dashboard', basic: true, standard: true },
  { name: 'Chair Dashboard', basic: true, standard: true },
  { name: 'Treasurer Dashboard', basic: true, standard: true },
  { name: 'Secretary Dashboard', basic: true, standard: true },
  { name: 'Trustee Dashboard', basic: true, standard: true },
  { name: 'Basic Event Management', basic: true, standard: true },
  { name: 'Meeting Management', basic: true, standard: true },
  { name: 'Member Management', basic: true, standard: true },
  { name: 'PTA Admin Dashboard', basic: false, standard: true },
  { name: 'Financial Tracking & Reports', basic: true, standard: true },
  { name: 'Expense Claims', basic: true, standard: true },
  { name: 'Cash Float Management', basic: true, standard: true },
  { name: 'Fund Allocations', basic: true, standard: true },
  { name: 'Affiliate Shop', basic: true, standard: true },
  { name: 'Fundraising Library', basic: true, standard: true },
  // Standard-only features
  { name: 'Advanced Events & Ticketing', basic: false, standard: true },
  { name: 'AI Event Planner', basic: false, standard: true },
  { name: 'Event Check-in with QR', basic: false, standard: true },
  { name: 'Communications Dashboard', basic: false, standard: true },
  { name: 'Community Dashboard', basic: false, standard: true },
  { name: 'Google Drive Integration', basic: false, standard: true },
  { name: 'Google Calendar Sync', basic: false, standard: true },
  { name: 'Google Meet Integration', basic: false, standard: true },
  { name: 'Digital Raffle', basic: false, standard: true },
  { name: 'Guessing Games', basic: false, standard: true },
  { name: 'Bingo Caller', basic: false, standard: true },
  { name: 'Public Fundraising Page', basic: false, standard: true },
  { name: 'Assets Dashboard', basic: false, standard: true },
  { name: 'Catering Dashboard', basic: false, standard: true },
  { name: 'Task Management', basic: false, standard: true },
];

export default function Features() {
  const [planFilter, setPlanFilter] = useState<PlanFilter>('all');

  const filteredCategories = featureCategories.map(category => ({
    ...category,
    features: category.features.filter(feature => {
      if (planFilter === 'all') return true;
      if (planFilter === 'basic') return feature.plan === 'basic' || feature.id === 'pta-admin-dashboard';
      // Standard includes all features except 'Basic Events' (show advanced events instead)
      if (planFilter === 'standard') return feature.id !== 'basic-events';
      return true;
    })
  })).filter(category => category.features.length > 0);

  const getPlanBadgeStyles = (plan: 'basic' | 'standard') => {
    if (plan === 'basic') {
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-800';
    }
    return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 border-purple-200 dark:border-purple-800';
  };

  return (
    <div className="space-y-16 animate-fade-in">
      {/* Plan Filter Section */}
      <section className="text-center">
        <h2 className="text-2xl font-bold text-foreground mb-6">Filter by Plan</h2>
        <Tabs value={planFilter} onValueChange={(v) => setPlanFilter(v as PlanFilter)} className="w-full max-w-md mx-auto">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="all" className="gap-2">
              All Features
            </TabsTrigger>
            <TabsTrigger value="basic" className="gap-2">
              <span className="hidden sm:inline">Basic</span>
              <Badge variant="outline" className="ml-1 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border-0 text-xs">
                £99/yr
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="standard" className="gap-2">
              <span className="hidden sm:inline">Standard</span>
              <Badge variant="outline" className="ml-1 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 border-0 text-xs">
                £129/yr
              </Badge>
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <p className="text-sm text-muted-foreground mt-4">
          {planFilter === 'all' && 'Showing all 30+ features across both plans'}
          {planFilter === 'basic' && 'Showing 14 features included in Basic Plan'}
          {planFilter === 'standard' && 'Showing all 30+ features included in Standard Plan (includes everything in Basic)'}
        </p>
      </section>

      {/* Feature Categories */}
      <section className="space-y-12">
        {filteredCategories.map((category) => {
          const CategoryIcon = category.icon;
          return (
            <div key={category.id} className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-primary/10">
                  <CategoryIcon className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-foreground">{category.name}</h2>
                  <p className="text-muted-foreground">{category.description}</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {category.features.map((feature) => {
                  const FeatureIcon = feature.icon;
                  const showBothBadges = planFilter === 'all' && (
                    showBothBadgesCategories.includes(category.id) || 
                    showBothBadgesFeatures.includes(feature.id)
                  );
                  const showSingleBadge = planFilter === 'all' && !showBothBadges;
                  return (
                    <Card key={feature.id} className="group hover:shadow-elevated transition-all duration-300 border-border/50 hover:border-primary/30">
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-muted group-hover:bg-primary/10 transition-colors">
                              <FeatureIcon className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                            </div>
                            <CardTitle className="text-lg">{feature.name}</CardTitle>
                          </div>
                          {showBothBadges && (
                            <div className="flex gap-1 shrink-0">
                              <Badge variant="outline" className={`text-xs ${getPlanBadgeStyles('basic')}`}>
                                Basic
                              </Badge>
                              <Badge variant="outline" className={`text-xs ${getPlanBadgeStyles('standard')}`}>
                                Standard
                              </Badge>
                            </div>
                          )}
                          {showSingleBadge && (
                            <Badge variant="outline" className={`text-xs shrink-0 ${getPlanBadgeStyles(feature.plan)}`}>
                              {feature.plan === 'basic' ? 'Basic' : 'Standard'}
                            </Badge>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent>
                        <CardDescription className="mb-3">{feature.description}</CardDescription>
                        <Accordion type="single" collapsible className="w-full">
                          <AccordionItem value="details" className="border-0">
                            <AccordionTrigger className="text-sm text-primary hover:no-underline py-2">
                              Learn more
                            </AccordionTrigger>
                            <AccordionContent>
                              <ul className="space-y-1.5 text-sm text-muted-foreground">
                                {feature.detailedDescription.map((detail, i) => (
                                  <li key={i} className="flex items-start gap-2">
                                    <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                                    {detail}
                                  </li>
                                ))}
                              </ul>
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          );
        })}
      </section>

      {/* Comparison Table */}
      <section className="space-y-6">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-foreground mb-2">Compare Plans</h2>
          <p className="text-muted-foreground">See exactly what's included in each plan</p>
        </div>

        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-[300px] font-semibold">Feature</TableHead>
                  <TableHead className="text-center w-[150px]">
                    <div className="flex flex-col items-center gap-1 pb-2">
                      <span className="font-semibold">Basic</span>
                      <Badge variant="outline" className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border-0">
                        £99.99/year
                      </Badge>
                    </div>
                  </TableHead>
                  <TableHead className="text-center w-[150px]">
                    <div className="flex flex-col items-center gap-1 pb-2">
                      <span className="font-semibold">Standard</span>
                      <Badge variant="outline" className="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 border-0">
                        £129.99/year
                      </Badge>
                      
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {comparisonFeatures.map((feature, index) => (
                  <TableRow key={index} className={index % 2 === 0 ? 'bg-muted/20' : ''}>
                    <TableCell className="font-medium">{feature.name}</TableCell>
                    <TableCell className="text-center">
                      {feature.basic ? (
                        <Check className="w-5 h-5 text-green-600 mx-auto" />
                      ) : (
                        <X className="w-5 h-5 text-muted-foreground/40 mx-auto" />
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {feature.standard ? (
                        <Check className="w-5 h-5 text-green-600 mx-auto" />
                      ) : (
                        <X className="w-5 h-5 text-muted-foreground/40 mx-auto" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      </section>

      {/* Bottom padding */}
      <div className="pb-16" />

    </div>
  );
}
